import React from 'react';
import PropTypes from 'prop-types';
import { over, get, set } from '@totalsoft/change-tracking-react';
import { useTranslation } from 'react-i18next';
import { TableCell, TableRow, Grid } from '@mui/material';
import { Autocomplete, TextField, IconButton } from '@totalsoft/rocket-ui';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { useFieldDecimalPlacesConfig } from 'hooks';

const FinancialProductResidualGridsTableRow = ({ canWrite, dataLens, index, onCancelEdit, onDelete, onSave, residualValueGridList, validation, isDirty }) => {
  const [t] = useTranslation();
  const data = dataLens |> get;
  const { cvsChPercent } = useFieldDecimalPlacesConfig();
  
  return (
    <TableRow>
      {data.isInEditing ? (
        <>
          <TableCell>
            <Autocomplete
              valueKey={'id'}
              labelKey={'name'}
              value={{ id: data.gridId, name: data.gridName }}
              options={residualValueGridList}
              onChange={event => {
                over(dataLens, oldState => ({
                  ...oldState,
                  gridId: event.id,
                  gridName: event.name,
                  maximumDecrease: event.maximumDecrease,
                  maximumIncrease: event.maximumIncrease
                }));
              }}
              error={!isValid(validation[index].gridId)}
              helperText={getErrors(validation[index].gridId)}
              required
            />
          </TableCell>
          <TableCell>
            <TextField
              fullWidth
              isNumeric
              decimalScale={cvsChPercent}
              value={data.maximumIncrease}
              onChange={dataLens.maximumIncrease |> set}
              error={!isValid(validation[index].maximumIncrease)}
              helperText={getErrors(validation[index].maximumIncrease)}
            />
          </TableCell>
          <TableCell>
            <TextField
              fullWidth
              isNumeric
              decimalScale={cvsChPercent}
              value={data.maximumDecrease}
              onChange={dataLens.maximumDecrease |> set}
              error={!isValid(validation[index].maximumDecrease)}
              helperText={getErrors(validation[index].maximumDecrease)}
            />
          </TableCell>
          <TableCell align='right'>
            <Grid container wrap='nowrap' justifyContent='flex-end'>
              <IconButton
                type='save'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.Save')}
                disabled={!isDirty || data.isSaving || !canWrite || !isValid(validation[index]) || !isValid(validation)}
                onClick={() => onSave(index)}
              />
              <IconButton
                type='cancel'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.CloseEdit')}
                onClick={() => onCancelEdit(data.financialProductXResidualValueGridId, index)}
              />
              <IconButton
                type='delete'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.Delete')}
                onClick={() => onDelete(data.financialProductXResidualValueGridId, index)}
                disabled={!canWrite}
              />
            </Grid>
          </TableCell>
        </>
      ) : (
        <>
          <TableCell>{data.gridName}</TableCell>
          <TableCell>{data.maximumIncrease}</TableCell>
          <TableCell>{data.maximumDecrease}</TableCell>
          <TableCell align='right'>
            <Grid container wrap='nowrap' justifyContent='flex-end'>
              <IconButton
                type='edit'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.Edit')}
                onClick={() => set(dataLens.isInEditing)(true)}
                disabled={!canWrite}
              />
              <IconButton
                type='delete'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.Delete')}
                onClick={() => onDelete(data.financialProductXResidualValueGridId, index)}
                disabled={!canWrite}
              />
            </Grid>
          </TableCell>
        </>
      )}
    </TableRow>
  );
};

FinancialProductResidualGridsTableRow.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  dataLens: PropTypes.object,
  index: PropTypes.number,
  onCancelEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  residualValueGridList: PropTypes.array.isRequired,
  validation: PropTypes.object.isRequired,
  isDirty: PropTypes.bool.isRequired
};

export default FinancialProductResidualGridsTableRow;
